import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, CheckCircle, Trash } from "@phosphor-icons/react";

const CATS = [
  { v: "general", l: "General" }, { v: "doctor", l: "Doctor" },
  { v: "shopping", l: "Shopping" }, { v: "hospital", l: "Hospital" },
  { v: "meal", l: "Meal" }, { v: "household", l: "Household" },
];

export default function FamilyTasks() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", due_date: "", category: "general" });

  const load = async () => {
    const [t, c] = await Promise.all([api.get("/family/tasks"), api.get("/family/circle")]);
    setItems(t.data);
    setMembers(c.data.members);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family/tasks", form);
      setForm({ title: "", description: "", assigned_to: "", due_date: "", category: "general" });
      setAdding(false);
      load();
    } catch { toast.error("Could not create."); }
  };

  const toggle = async (id) => { await api.post(`/family/tasks/${id}/toggle`); load(); };
  const del = async (id) => { await api.delete(`/family/tasks/${id}`); load(); };

  const open = items.filter((t) => !t.is_done);
  const done = items.filter((t) => t.is_done);

  return (
    <div className="space-y-8" data-testid="family-tasks-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-saffron mb-2">Together</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Family tasks</h1>
          <p className="text-ink-700 mt-2">Shared work, shared progress. Nothing falls between the cracks.</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="btn-primary" data-testid="add-task-btn"><Plus weight="thin" size={18} /> New task</button>
      </header>

      {adding && (
        <form onSubmit={submit} className="vansha-card p-6 grid sm:grid-cols-2 gap-5" data-testid="task-form">
          <div className="sm:col-span-2"><label className="overline mb-2 block">Title</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-line" placeholder="Pick up prenatal vitamins" data-testid="task-title" /></div>
          <div><label className="overline mb-2 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line bg-transparent" data-testid="task-cat">
              {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div><label className="overline mb-2 block">Due date</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="input-line" data-testid="task-due" /></div>
          <div className="sm:col-span-2"><label className="overline mb-2 block">Assign to</label>
            <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="input-line bg-transparent" data-testid="task-assign">
              <option value="">— Anyone —</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><label className="overline mb-2 block">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-line" rows="2" data-testid="task-desc" /></div>
          <div className="sm:col-span-2 flex gap-3 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="task-submit">Save</button>
          </div>
        </form>
      )}

      <section data-testid="open-section">
        <h2 className="font-serif text-2xl mb-4">Open ({open.length})</h2>
        {open.length === 0 ? <p className="text-ink-500" data-testid="tasks-empty">All clear. Add one when needed.</p> : (
          <div className="space-y-3">
            {open.map((t) => <TaskCard key={t.id} t={t} members={members} onToggle={toggle} onDel={del} />)}
          </div>
        )}
      </section>

      {done.length > 0 && (
        <section data-testid="done-section">
          <h2 className="font-serif text-2xl mb-4">Done ({done.length})</h2>
          <div className="space-y-3">
            {done.map((t) => <TaskCard key={t.id} t={t} members={members} onToggle={toggle} onDel={del} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function TaskCard({ t, members, onToggle, onDel }) {
  const assignee = members.find((m) => m.id === t.assigned_to);
  return (
    <div className={`vansha-card p-5 flex items-start gap-4 ${t.is_done ? "opacity-60" : ""}`} data-testid={`task-${t.id}`}>
      <button onClick={() => onToggle(t.id)} className={`w-7 h-7 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center ${t.is_done ? "bg-terracotta border-terracotta" : "border-ink-20"}`} data-testid={`toggle-${t.id}`}>
        {t.is_done && <Check weight="bold" size={16} className="text-white" />}
      </button>
      <div className="flex-1">
        <p className="overline text-saffron">{t.category}</p>
        <h3 className={`font-serif text-xl ${t.is_done ? "line-through" : ""}`}>{t.title}</h3>
        {t.description && <p className="text-sm text-ink-700 mt-1">{t.description}</p>}
        <div className="flex gap-4 mt-2 text-xs text-ink-500">
          {assignee && <span>For {assignee.name}</span>}
          {t.due_date && <span>Due {t.due_date}</span>}
          {t.created_by_name && <span>By {t.created_by_name}</span>}
        </div>
      </div>
      <button onClick={() => onDel(t.id)} className="btn-ghost p-2"><Trash weight="thin" size={16} /></button>
    </div>
  );
}
